import { useAtom, useSetAtom } from 'jotai';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import RoundBadge from '@/components/Common/Badge/RoundBadge';
import DualFooterButton from '@/components/Common/Button/DualFooterButton';
import RadioList, { OptionType } from '@/components/Common/Input/Radio/RadioList';
import TextInputLabelOff from '@/components/Common/Input/TextInputLabelOff';
import BottomSheetPopup from '@/components/Common/Popup/BottomSheetPopup';
import MenuHeader from '@/components/Header/MenuHeader';
import { ChevronDownIcon } from '@/components/Icon';
import ConfirmExitPopup from '@/components/Products/ConfirmExitPopup';
import { colors } from '@/const/colors';
import { MIN_LENGTH_ERROR_MESSAGE } from '@/const/errorMessage';
import { SALESTYPE } from '@/const/products';
import { positiveFloatRegex } from '@/const/regex';
import { SELL_CAR_DEFAULT_TITLE } from '@/const/title';
import { PRODUCT_SALE_URL } from '@/const/url';
import { useProductEnumContext } from '@/contexts/Products/ProductEnumContext';
import useUpdateProductFormMutation from '@/hooks/query/useUpdateProductFormMutation';
import useBlockNavigation from '@/hooks/useBlockNavigation';
import useExitConfirmationPopup from '@/hooks/useExitConfirmationPopup';
import useFetchProductData from '@/hooks/useFetchProductData';
import DefaultHelmet from '@/metadatas/DefaultHelmet';
import { productsFormAtom, useResetProductsForm } from '@/store/products';
import {
  getSelectableLoadedList,
  isAllLoadedInfoAvailable,
  isLengthOnlyLoadedType,
  shouldShowOnlyLength,
} from '@/utils/Products/productUtils';
import { formatValueToDecimalPlaces, updateSearchParams } from '@/utils/common';
import { validateLoadedInnerLength } from '@/utils/validation';

type SalesTypeKey = keyof typeof SALESTYPE;

const LoadedForm = () => {
  const { productEnum } = useProductEnumContext();
  const navigate = useNavigate();
  const { id } = useParams();

  const [productFormData] = useAtom(productsFormAtom);
  const setProductFormData = useSetAtom(productsFormAtom);
  const resetProductsForm = useResetProductsForm();

  const [prevData, setPrevData] = useState<ProductDetailResponse | null>(null);

  const tonsNum = Number(productFormData?.tons);
  const isOneTonRange = tonsNum === 1 || tonsNum === 1.1 || tonsNum === 1.2;
  const loadedEnumKey = isOneTonRange ? 'oneTonsLoaded' : 'loaded';

  const getValidLoadedCodeSet = () => {
    if (!productEnum) return new Set<string>();
    if (isOneTonRange) {
      return new Set((productEnum.oneTonsLoaded ?? []).map((item) => String(item.code)));
    }
    return new Set(getSelectableLoadedList(productEnum.loaded, tonsNum).map((item) => String(item.code)));
  };

  const getSelectedLoadedDetailList = (): EnumPresenter[] => {
    if (!isOneTonRange || !productEnum || !productFormData?.loaded?.code) return [];
    const found = (productEnum.oneTonsLoaded ?? []).find((item) => item.code === productFormData.loaded?.code);
    return found?.loadedDetail ?? [];
  };

  const selectedLoadedDetailList = getSelectedLoadedDetailList();
  const hasLoadedDetail = selectedLoadedDetailList.length > 0;

  useEffect(() => {
    if (!productFormData?.loaded?.code) return;
    if (!productFormData?.tons) return;
    const validLoadedCodeSet = getValidLoadedCodeSet();
    if (validLoadedCodeSet.size === 0) return;

    if (!validLoadedCodeSet.has(String(productFormData.loaded.code))) {
      setProductFormData({
        ...productFormData,
        loaded: undefined,
        loadedDetail: undefined,
      } as unknown as ProductDetailResponse);
    }
  }, [isOneTonRange, tonsNum, productEnum, productFormData?.tons, productFormData?.loaded?.code]);

  useEffect(() => {
    if (!isOneTonRange) return;
    if (!productEnum) return;
    if (!productFormData?.loaded?.code) return;
    if (!productFormData?.loadedDetail?.code) return;

    const loadedItem = (productEnum.oneTonsLoaded ?? []).find((item) => item.code === productFormData.loaded?.code);
    const validDetailCodeSet = new Set((loadedItem?.loadedDetail ?? []).map((item) => String(item.code)));

    if (validDetailCodeSet.size === 0 || !validDetailCodeSet.has(String(productFormData.loadedDetail.code))) {
      setProductFormData({
        ...productFormData,
        loadedDetail: undefined,
      } as ProductDetailResponse);
    }
  }, [isOneTonRange, productEnum, productFormData?.loaded?.code, productFormData?.loadedDetail?.code]);

  const checkForChanges = () => {
    return (
      prevData?.loaded?.code !== productFormData.loaded?.code ||
      (isOneTonRange && prevData?.loadedDetail?.code !== productFormData.loadedDetail?.code) ||
      prevData?.loadedInnerLength !== productFormData.loadedInnerLength ||
      prevData?.loadedInnerArea !== productFormData.loadedInnerArea ||
      prevData?.loadedInnerHeight !== productFormData.loadedInnerHeight
    );
  };

  const buildLoadedRequest = () => {
    const request: {
      id: number;
      loaded: string | undefined;
      loadedDetail: string;
      loadedInnerLength: number;
      loadedInnerArea: number;
      loadedInnerHeight: number;
    } = {
      id: Number(productFormData.id),
      loaded: productFormData.loaded?.code,
      loadedDetail: '',
      loadedInnerLength: Number(productFormData.loadedInnerLength),
      loadedInnerArea: Number(productFormData.loadedInnerArea),
      loadedInnerHeight: Number(productFormData.loadedInnerHeight),
    };

    if (isOneTonRange) {
      request.loadedDetail = productFormData.loadedDetail?.code ? productFormData.loadedDetail.code : '';
    }

    return request;
  };

  const saveChanges = () => {
    updateProductTemporarySaveMutation.mutate(buildLoadedRequest());
  };

  const { isShowExitConfirmPopup, openExitConfirmPopup, closeExitConfirmPopup, confirmExit } = useExitConfirmationPopup(
    checkForChanges,
    saveChanges,
    resetProductsForm,
  );

  useBlockNavigation(openExitConfirmPopup);

  const [isOpenOptionModal, setIsOpenOptionModal] = useState(false);
  const [optionData, setOptionData] = useState<OptionType[]>([]);

  const loadedLengthInputRef = useRef<HTMLInputElement>(null);
  const loadedAreaInputRef = useRef<HTMLInputElement>(null);
  const loadedHeightInputRef = useRef<HTMLInputElement>(null);

  const [loadedInnerLengthError, setLoadedInnerLengthError] = useState<boolean>(false);
  const [loadedInnerLengthErrorMsg, setLoadedInnerLengthErrorMsg] = useState('');
  const [loadedInnerAreaError, setLoadedInnerAreaError] = useState<boolean>(false);
  const [loadedInnerAreaErrorMsg, setLoadedInnerAreaErrorMsg] = useState('');
  const [loadedInnerHeightError, setLoadedInnerHeightError] = useState<boolean>(false);
  const [loadedInnerHeightErrorMsg, setLoadedInnerHeightErrorMsg] = useState('');

  useFetchProductData(id, productFormData.id === 0);

  useEffect(() => {
    setPrevData(productFormData);
  }, []);

  const updateProductTemporarySaveMutation = useUpdateProductFormMutation({});

  const handleClickExitBtn = () => {
    const hasChanges = checkForChanges();

    if (hasChanges) {
      updateProductTemporarySaveMutation.mutate(buildLoadedRequest());
    } else {
      navigate(PRODUCT_SALE_URL, { replace: true });
      resetProductsForm();
    }
  };

  const updateProductNextStepMutation = useUpdateProductFormMutation({
    url: `/products/sales/axis/${id}`,
  });

  const handleClickNext = () => {
    const hasChanges = checkForChanges();

    if (hasChanges) {
      updateProductNextStepMutation.mutate(buildLoadedRequest());
    } else {
      navigate(`/products/sales/axis/${id}`, { replace: true });
    }
  };

  const handleClickPrev = () => {
    navigate(`/products/sales/tons/${id}`, { replace: true });
  };

  const [optionModalKey, setOptionModalKey] = useState<'loaded' | 'loadedDetail'>('loaded');

  const handleCloseOptionModal = () => {
    setIsOpenOptionModal(false);
  };

  const handleClickSelectBox = (key: 'loaded' | 'loadedDetail') => {
    if (!productEnum) return;
    setOptionModalKey(key);

    if (key === 'loaded') {
      if (isOneTonRange) {
        const list = (productEnum.oneTonsLoaded ?? []).map((item) => ({ code: item.code, desc: item.desc }));
        setOptionData(list as OptionType[]);
      } else {
        setOptionData(getSelectableLoadedList(productEnum.loaded, tonsNum) as OptionType[]);
      }
    } else if (key === 'loadedDetail') {
      if (!hasLoadedDetail) return;
      setOptionData(selectedLoadedDetailList as OptionType[]);
    }

    setIsOpenOptionModal(true);
  };

  const handleClickOption = (item: OptionType, key: 'loaded' | 'loadedDetail') => {
    if (key === 'loaded') {
      setProductFormData({
        ...productFormData,
        loaded: { code: String(item.code), desc: String(item.desc) },
        loadedDetail: undefined,
      } as unknown as ProductDetailResponse);
    } else if (key === 'loadedDetail') {
      setProductFormData({
        ...productFormData,
        loadedDetail: { code: String(item.code), desc: String(item.desc) },
      } as ProductDetailResponse);
    }

    setTimeout(() => {
      setIsOpenOptionModal(false);
      if (
        key === 'loaded' &&
        loadedLengthInputRef &&
        loadedLengthInputRef.current &&
        !productFormData?.loadedInnerLength
      ) {
        loadedLengthInputRef.current.focus();
      }
    }, 200);
  };

  const handleChangeLoadedInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const decimalPlaces = 2;
    const formattedValue = formatValueToDecimalPlaces(value, decimalPlaces);
    const isInvalidInput = formattedValue.startsWith('0') && formattedValue.length > 1;

    if (isInvalidInput) {
      setProductFormData({ ...productFormData, [name]: '0' } as ProductDetailResponse);
      return;
    }

    if (name === 'loadedInnerLength' && productFormData) {
      if (!formattedValue.trim()) {
        setLoadedInnerLengthError(false);
        setLoadedInnerLengthErrorMsg('');
      } else {
        const { hasError, errorMsg } = validateLoadedInnerLength(
          Number(formattedValue),
          Number(productFormData.tons),
          productFormData.manufacturerCategories.code,
          productFormData.model.id,
        );
        setLoadedInnerLengthError(hasError);
        setLoadedInnerLengthErrorMsg(errorMsg);
      }
    } else if (name === 'loadedInnerArea' || name === 'loadedInnerHeight') {
      // 너비와 높이는 선택사항이므로 빈 값일 때는 에러를 설정하지 않음
      const isBelowMinimum = formattedValue && Number(formattedValue) < 1;
      setLoadedInnerAreaError(name === 'loadedInnerArea' && !!isBelowMinimum);
      setLoadedInnerAreaErrorMsg(name === 'loadedInnerArea' && isBelowMinimum ? MIN_LENGTH_ERROR_MESSAGE : '');
      setLoadedInnerHeightError(name === 'loadedInnerHeight' && !!isBelowMinimum);
      setLoadedInnerHeightErrorMsg(name === 'loadedInnerHeight' && isBelowMinimum ? MIN_LENGTH_ERROR_MESSAGE : '');
    }

    if (positiveFloatRegex.test(formattedValue.trim()) || formattedValue.trim() === '') {
      const str = formattedValue.trim() === '' ? null : formattedValue.trim();
      setProductFormData({ ...productFormData, [name]: str } as ProductDetailResponse);
    }
  };

  const handleBlurInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: string) => {
    let { value } = e.target;
    const valueNum = Number(value);

    if (valueNum >= 100) {
      const { updatedParams, error, errorMsg } = updateSearchParams(productFormData, key, valueNum);
      setProductFormData(updatedParams);
      switch (key) {
        case 'loadedInnerLength':
          setLoadedInnerLengthError(error);
          setLoadedInnerLengthErrorMsg(errorMsg);
          break;
        case 'loadedInnerArea':
          setLoadedInnerAreaError(error);
          setLoadedInnerAreaErrorMsg(errorMsg);
          break;
        case 'loadedInnerHeight':
          setLoadedInnerHeightError(error);
          setLoadedInnerHeightErrorMsg(errorMsg);
          break;
      }
    }
  };

  useEffect(() => {
    if (!productFormData?.loadedInnerLength) {
      setLoadedInnerLengthError(false);
      setLoadedInnerLengthErrorMsg('');
      return;
    }
    const { hasError, errorMsg } = validateLoadedInnerLength(
      Number(productFormData.loadedInnerLength),
      Number(productFormData.tons),
      productFormData.manufacturerCategories.code,
      productFormData.model.id,
    );
    setLoadedInnerLengthError(hasError);
    setLoadedInnerLengthErrorMsg(errorMsg);
  }, [productFormData?.loadedInnerLength, productFormData?.tons]);

  const scrollIntoView = (e?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e) {
      const { name } = e.target;
      if (name === 'loadedInnerLength' && prevData?.loadedInnerLength === null) {
        loadedLengthInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'loadedInnerArea' && prevData?.loadedInnerArea === null) {
        loadedAreaInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'loadedInnerHeight' && prevData?.loadedInnerHeight === null) {
        loadedHeightInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }
  };

  useLayoutEffect(() => {
    const detectMobileKeyboard = () => {
      scrollIntoView();
    };
    window.addEventListener('resize', detectMobileKeyboard);
    return () => {
      window.removeEventListener('resize', detectMobileKeyboard);
    };
  });

  const isDisabledBtn = () => {
    if (!productFormData?.loaded) {
      return true;
    } else if (isLengthOnlyLoadedType(productFormData)) {
      return !productFormData?.loadedInnerLength || loadedInnerLengthError;
    } else {
      // 길이는 필수, 너비와 높이는 선택사항 (입력했다면 유효성 검사만 수행)
      return (
        !productFormData?.loadedInnerLength || loadedInnerLengthError || loadedInnerAreaError || loadedInnerHeightError
      );
    }
  };

  const isShowOnlyLength = (prevData: any, productFormData: any) => {
    const isShowAllLoadedInfo = isAllLoadedInfoAvailable(prevData) || isAllLoadedInfoAvailable(productFormData);
    return !isShowAllLoadedInfo && shouldShowOnlyLength(productFormData);
  };

  return (
    <>
      <DefaultHelmet page="sale" />

      <MenuHeader title={SALESTYPE[productFormData.type?.code as SalesTypeKey] || SELL_CAR_DEFAULT_TITLE} />
      <div className="flex flex-col w-full pt-[60px] p-4">
        <div className="pt-6 text-gray-8">
          <div className="flex justify-between">
            <h3 className="text-2xl font-bold break-keep">
              적재함 종류와 <br />
              길이(내측 길이)를 입력해주세요.
            </h3>
            <RoundBadge text="3/9"></RoundBadge>
          </div>

          <p className="pt-2 text-sm break-keep">다음 단계로 넘어가기 전, 내측 길이를 한번 더 확인해주세요.</p>

          <div className="text-base pt-[30px] pb-24">
            <div className="w-full py-3" onClick={() => handleClickSelectBox('loaded')}>
              <label className="block mb-2 text-base font-medium text-gray-8">
                적재함 종류<span className="ml-1 text-red">*</span>
              </label>
              <TextInputLabelOff
                name="loaded"
                placeholder="적재함 종류 선택"
                value={productFormData?.loaded?.desc || ''}
                fontSize={18}
                height={36}
                type="text"
                readOnly
                suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
              ></TextInputLabelOff>
            </div>
            {hasLoadedDetail && (
              <div className="w-full py-3" onClick={() => handleClickSelectBox('loadedDetail')}>
                <label className="block mb-2 text-base font-medium text-gray-8">세부 적재함 종류</label>
                <TextInputLabelOff
                  name="loadedDetail"
                  placeholder="세부 적재함 종류 선택"
                  value={productFormData?.loadedDetail?.desc || ''}
                  fontSize={18}
                  height={36}
                  type="text"
                  readOnly
                  suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
                ></TextInputLabelOff>
              </div>
            )}
            {isShowOnlyLength(prevData, productFormData) ? (
              <div className="flex items-center justify-center w-full py-3">
                <TextInputLabelOff
                  ref={loadedLengthInputRef}
                  name="loadedInnerLength"
                  placeholder="길이 입력"
                  value={productFormData?.loadedInnerLength ? String(productFormData?.loadedInnerLength) : ''}
                  onChange={(e) => handleChangeLoadedInput(e)}
                  onBlur={(e) => handleBlurInput(e, 'loadedInnerLength')}
                  onFocus={(e) => scrollIntoView(e)}
                  error={loadedInnerLengthError}
                  errorMsg={loadedInnerLengthErrorMsg}
                  fontSize={18}
                  height={36}
                  maxLength={5}
                  inputMode="numeric"
                  suffix="m"
                ></TextInputLabelOff>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center w-full py-3">
                  <label className="text-base font-medium text-gray-8 min-w-[60px]">
                    길이<span className="ml-1 text-red">*</span>
                  </label>
                  <TextInputLabelOff
                    ref={loadedLengthInputRef}
                    name="loadedInnerLength"
                    placeholder="길이 입력"
                    value={productFormData?.loadedInnerLength ? String(productFormData?.loadedInnerLength) : ''}
                    onChange={(e) => handleChangeLoadedInput(e)}
                    onBlur={(e) => handleBlurInput(e, 'loadedInnerLength')}
                    onFocus={(e) => scrollIntoView(e)}
                    error={loadedInnerLengthError}
                    errorMsg={loadedInnerLengthErrorMsg}
                    fontSize={18}
                    height={36}
                    inputMode="numeric"
                    suffix="m"
                    maxLength={5}
                  ></TextInputLabelOff>
                </div>
                <div className="flex items-center justify-center w-full py-3">
                  <label className="text-base font-medium text-gray-8 min-w-[60px]">너비</label>
                  <TextInputLabelOff
                    ref={loadedAreaInputRef}
                    name="loadedInnerArea"
                    placeholder="너비 입력"
                    value={productFormData?.loadedInnerArea ? String(productFormData?.loadedInnerArea) : ''}
                    onChange={(e) => handleChangeLoadedInput(e)}
                    onBlur={(e) => handleBlurInput(e, 'loadedInnerArea')}
                    onFocus={(e) => scrollIntoView(e)}
                    error={loadedInnerAreaError}
                    errorMsg={loadedInnerAreaErrorMsg}
                    fontSize={18}
                    height={36}
                    inputMode="numeric"
                    suffix="m"
                    maxLength={5}
                  ></TextInputLabelOff>
                </div>
                <div className="flex items-center justify-center w-full py-3">
                  <label className="text-base font-medium text-gray-8 min-w-[60px]">높이</label>
                  <TextInputLabelOff
                    ref={loadedHeightInputRef}
                    name="loadedInnerHeight"
                    placeholder="높이 입력"
                    value={productFormData?.loadedInnerHeight ? String(productFormData?.loadedInnerHeight) : ''}
                    onChange={(e) => handleChangeLoadedInput(e)}
                    onBlur={(e) => handleBlurInput(e, 'loadedInnerHeight')}
                    onFocus={(e) => scrollIntoView(e)}
                    error={loadedInnerHeightError}
                    errorMsg={loadedInnerHeightErrorMsg}
                    fontSize={18}
                    height={36}
                    inputMode="numeric"
                    suffix="m"
                    maxLength={5}
                  ></TextInputLabelOff>
                </div>
              </>
            )}
          </div>
        </div>
        <DualFooterButton
          leftButtonText="이전"
          onClickLeftButton={handleClickPrev}
          rightButtonText="다음(가변축)"
          disabledRight={isDisabledBtn()}
          onClickRightButton={handleClickNext}
        ></DualFooterButton>
      </div>

      <BottomSheetPopup
        isOpen={isOpenOptionModal}
        onClose={handleCloseOptionModal}
        title={optionModalKey === 'loadedDetail' ? '세부 적재함 종류' : '적재함 종류'}
      >
        <div className="px-4 pb-8">
          <RadioList
            name={optionModalKey}
            list={optionData}
            value={
              optionModalKey === 'loadedDetail'
                ? productFormData?.loadedDetail?.code || ''
                : productFormData?.loaded?.code || ''
            }
            onChange={(val) => {
              handleClickOption(val, optionModalKey);
            }}
            horizontal={false}
          ></RadioList>
        </div>
      </BottomSheetPopup>

      <ConfirmExitPopup isShow={isShowExitConfirmPopup} onClose={closeExitConfirmPopup} onConfirm={confirmExit} />
    </>
  );
};

export default LoadedForm;
